import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO } from '../lib/useSEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuthStore } from '../lib/auth';
import { toast } from 'sonner';
import { User } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function CompleteProfile() {
  const navigate = useNavigate();
  const { completeProfile, user, isAuthenticated } = useAuthStore();
  const [displayName, setDisplayName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useSEO('Complete Profile | JepPrep', 'Complete your JepPrep profile to get started with trivia training.', { noindex: true });

  // Redirect if not authenticated or user already has a display name
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    } else if (user?.display_name) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Show nothing while redirecting
  if (!isAuthenticated || user?.display_name) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (displayName.trim().length < 2) {
      toast.error('Username must be at least 2 characters');
      return;
    }
    if (displayName.trim().length > 30) {
      toast.error('Username must be 30 characters or less');
      return;
    }
    setIsSubmitting(true);
    const result = await completeProfile(displayName.trim());
    setIsSubmitting(false);
    if (result.success) {
      toast.success('Welcome to JepPrep!');
      navigate('/dashboard', { replace: true });
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[hsl(var(--background))]">
      <Card className="w-full max-w-md bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
        <CardHeader className="text-center">
          <img src={LOGO_URL} alt="JepPrep" className="h-16 mx-auto mb-4" />
          <CardTitle className="text-2xl font-display text-white">Almost There!</CardTitle>
          <CardDescription className="text-gray-400">Choose a username to get started</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="displayName" className="text-gray-300">Username</Label>
              <div className="relative">
                <Input
                  id="displayName"
                  type="text"
                  placeholder="Your display name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  autoFocus
                  className="bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white pl-10"
                  data-testid="complete-profile-name-input"
                />
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500">This is how other players will see you. 2-30 characters.</p>
            </div>
            <Button
              type="submit"
              className="w-full bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white"
              disabled={isSubmitting}
              data-testid="complete-profile-submit"
            >
              {isSubmitting ? 'Setting up...' : 'Start Training'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
